import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Divider from '@amzn/meridian/divider';
import sparklesTokens from '@amzn/meridian-tokens/base/icon/sparkles-large';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { useTranslation } from 'src/arb-wrapper';
import { useVictoryTheme } from '@amzn/meridian';
import { colorBlue400 } from '@amzn/meridian-tokens/base/color';
import { VictoryLine, VictoryChart, VictoryAxis, VictoryTooltip, VictoryVoronoiContainer } from 'victory';
import { PublisherDisplay } from 'src/components/common/PublisherDisplay';

const hasPlatformData = (v?: string | null): boolean => Boolean(v && v !== '{}' && v !== '[]');

export interface SocialSignalsPanelData {
  titleName: string;
  titleId: string;
  trendText: string;
  signalTimestamp: string;
  trendSummary: string;
  sentimentSummary?: string | null;
  llmReason: string;
  imageUrl?: string | null;
  imageLoading?: boolean;
  sourcePlatforms?: string | null;
  tiktokPlatformMetadata?: string | null;
  serpapiPlatformMetadata?: string | null;
  trendingHistory?: string | null;
  volumeRaw?: string | null;
}

interface SocialSignalsDetailPanelProps {
  data: SocialSignalsPanelData;
  onClose: () => void;
}

export const SocialSignalsDetailPanel: React.FC<SocialSignalsDetailPanelProps> = ({ data, onClose }) => {
  const { t } = useTranslation();

  return (
    <div data-testid="social-signals-side-panel" style={{ height: '100%' }}>
    <style>{'@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }'}</style>
    <Column height="100%" heights={['fit', 'fill', 'fit']}>
      <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="400">
        <Row alignmentVertical="center" spacing="200">
          <span style={{ color: '#0073bb', display: 'inline-flex' }}><Icon tokens={sparklesTokens} /></span>
          <Text type="h300"><span style={{ color: '#0073bb' }}><b>{t('social_signals_panel_title')}</b></span></Text>
        </Row>
        <Button type="icon" onClick={onClose}>
          <Icon tokens={closeTokens}>{t('social_signals_panel_close')}</Icon>
        </Button>
      </Row>
      <Column overflowY="auto" spacing="400" spacingInset="none 400 400 400">
        {data.imageLoading
          ? (
          <div data-testid="image-skeleton" style={{ width: '100%', aspectRatio: '16/9', borderRadius: 8, backgroundColor: '#E9EBEF', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
            )
          : data.imageUrl
            ? (
          <img
            alt={data.trendText}
            src={data.imageUrl}
            style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', borderRadius: 8, display: 'block', flexShrink: 0 }}
          />
              )
            : null}
        <Column spacing="100">
          <Text type="h200">{data.trendText}</Text>
          <Text color="secondary" type="b200">{data.signalTimestamp}</Text>
        </Column>
        <Divider />
        <PanelSection label={t('social_signals_panel_trend_overview')} text={data.trendSummary} />
        <PanelSection label={t('social_signals_panel_audience_sentiment')} text={data.sentimentSummary} />
        <Divider />
        {(hasPlatformData(data.tiktokPlatformMetadata) || hasPlatformData(data.serpapiPlatformMetadata)) && (
          <Column spacing="200">
            <Text type="b300"><b>{t('social_signals_panel_source_platforms')}</b></Text>
            <TikTokSection tiktokData={data.tiktokPlatformMetadata} trendingHistory={data.trendingHistory} />
            <GoogleSearchMetrics data={data.serpapiPlatformMetadata} volumeRaw={data.volumeRaw} />
          </Column>
        )}
      </Column>
      <Column spacingInset="400">
        <Button type="secondary" onClick={onClose}>{t('social_signals_panel_close')}</Button>
      </Column>
    </Column>
    </div>
  );
};

const PanelSection: React.FC<{ label: string; text: string | null | undefined }> = ({ label, text }) => (
  <Column spacing="200">
    <Text type="b300"><b>{label}</b></Text>
    <div style={{ backgroundColor: '#E9EBEF', borderRadius: 8, padding: 12 }}>
      <Text type="b300">{text ?? '-'}</Text>
    </div>
  </Column>
);

const formatNumber = (n: number): string => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const TikTokSection: React.FC<{ tiktokData?: string | null; trendingHistory?: string | null }> = ({ tiktokData, trendingHistory }) => {
  const { t } = useTranslation();
  const theme = useVictoryTheme({ showIndependentGrid: false });
  if (!tiktokData || tiktokData === '[]' || tiktokData === '{}') return null;
  try {
    const meta = JSON.parse(tiktokData);
    const history = trendingHistory ? JSON.parse(trendingHistory) as Array<{ hashtag_date: string; views_daily: number; rank_position_daily: string | null }> : [];
    const chartData = history.map((h) => ({ x: h.hashtag_date.slice(5), y: h.views_daily }));

    return (
      <div style={{ backgroundColor: '#E9EBEF', borderRadius: 8, padding: 12 }}>
        <Column spacing="200">
          <Row alignmentVertical="center" spacing="200">
            <PublisherDisplay iconOnly publisher="tiktok" />
            <Text type="b300"><b>{t('social_signals_platform_tiktok')}</b></Text>
          </Row>
          <Row spacing="400" wrap="down">
            <Text type="b200"><span style={{ color: colorBlue400, fontWeight: 600 }}>{t('social_signals_posts')}: {formatNumber(Number(meta.posts) || 0)}</span></Text>
            <Text type="b200"><span style={{ color: colorBlue400, fontWeight: 600 }}>{t('social_signals_views')}: {formatNumber(Number(meta.views_lifetime) || 0)}</span></Text>
          </Row>
          {meta.audience_ages && (
            <Text color="secondary" type="b200">
              {t('social_signals_audience')}: {meta.audience_ages.map((a: { age: string; percentage: number }) => `${a.age} (${Math.round(a.percentage * 100)}%)`).join(', ')}
            </Text>
          )}
          {chartData.length > 1 && (
            <div style={{ height: 200, marginTop: 8 }}>
              <VictoryChart
                containerComponent={<VictoryVoronoiContainer labelComponent={<VictoryTooltip cornerRadius={4} />} labels={({ datum }: { datum: { x: string; y: number } }) => `${datum.x}: ${formatNumber(datum.y)}`} />}
                domainPadding={{ x: 10 }}
                height={200}
                padding={{ top: 10, bottom: 35, left: 60, right: 10 }}
                theme={theme}
                width={350}
              >
                <VictoryAxis style={{ tickLabels: { fontSize: 12, angle: -30 } }} tickFormat={(t: string, i: number) => i % 5 === 0 ? t : ''} />
                <VictoryAxis dependentAxis style={{ tickLabels: { fontSize: 12 } }} tickFormat={(t: number) => formatNumber(t)} />
                <VictoryLine data={chartData} style={{ data: { stroke: colorBlue400, strokeWidth: 2 } }} />
              </VictoryChart>
            </div>
          )}
        </Column>
      </div>
    );
  } catch { return null; }
};

const GoogleSearchMetrics: React.FC<{ data?: string | null; volumeRaw?: string | null }> = ({ data, volumeRaw }) => {
  const { t } = useTranslation();
  if (!data || data === '{}' || data === '[]') return null;
  try {
    const meta = JSON.parse(data);
    let googleVolume: number | null = null;
    if (volumeRaw) {
      try { googleVolume = JSON.parse(volumeRaw).serpapi_google_trends ?? null; } catch { /* ignore */ }
    }
    const startDate = meta.start_timestamp ? new Date(meta.start_timestamp * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' }) : null;

    return (
      <div style={{ backgroundColor: '#E9EBEF', borderRadius: 8, padding: 12 }}>
        <Column spacing="200">
          <Row alignmentVertical="center" spacing="200">
            <PublisherDisplay iconOnly publisher="google" />
            <Text type="b300"><b>{t('social_signals_platform_google')}</b></Text>
          </Row>
          <Row spacing="400" wrap="down">
            {meta.increase_pct != null && <Text type="b200"><span style={{ color: colorBlue400, fontWeight: 600 }}>↑ +{meta.increase_pct}%</span></Text>}
            {googleVolume != null && <Text type="b200"><span style={{ color: colorBlue400, fontWeight: 600 }}>{t('social_signals_volume')}: {formatNumber(googleVolume)}</span></Text>}
          </Row>
          {meta.categories?.[0]?.name && <Text color="secondary" type="b200">{t('social_signals_category')}: {meta.categories[0].name}</Text>}
          {startDate && <Text color="secondary" type="b200">{t('social_signals_trending_since')}: {startDate}</Text>}
          {meta.related_queries?.length > 0 && (
            <Text color="secondary" type="b200">{t('social_signals_related')}: {meta.related_queries.slice(0, 5).join(', ')}</Text>
          )}
        </Column>
      </div>
    );
  } catch { return null; }
};
